import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../components/common/Button';
import { PlayingIconBtn, PlayingDisclosure } from './PlayingDisclosure';

/**
 * Under-board: only frequent, one-click actions (flip / analyze / sound).
 * Everything else lives in PlayingGameOptions — a side-panel disclosure, not a ⋯ menu.
 */
export default function PlayingBoardTools({
  onFlip,
  onAnalyze,
  canAnalyze,
  analysisBusy,
  analysisOn,
  analysisProgress,
  soundMuted,
  onToggleSound,
  rematchSlot,
}) {
  const { t } = useTranslation();

  return (
    <div className="playing-game-toolbar">
      <div className="playing-icon-bar">
        <PlayingIconBtn label={t('playing.flipBoard')} onClick={onFlip}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
        </PlayingIconBtn>
        {canAnalyze && (
          <PlayingIconBtn
            label={analysisBusy
              ? t('playing.analyzing', { pct: analysisProgress })
              : analysisOn
                ? t('playing.reanalyze')
                : t('playing.analyze')}
            disabled={analysisBusy}
            active={analysisOn}
            onClick={onAnalyze}
          >
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </PlayingIconBtn>
        )}
        <PlayingIconBtn
          label={soundMuted ? t('playing.soundOn') : t('playing.soundOff')}
          active={!soundMuted}
          onClick={onToggleSound}
        >
          {soundMuted ? (
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M11 5L6 9H2v6h4l5 4V5z" />
              <line x1="23" y1="9" x2="17" y2="15" />
              <line x1="17" y1="9" x2="23" y2="15" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
            </svg>
          )}
        </PlayingIconBtn>
      </div>
      {rematchSlot && (
        <div className="playing-icon-bar playing-icon-bar--rematch">
          {rematchSlot}
        </div>
      )}
    </div>
  );
}

/** Side-panel list — expands in place (never a floating menu that clips under the footer). */
export function PlayingGameOptions({
  open,
  onOpenChange,
  onDownloadPgn,
  canPgn,
  onCopyLink,
  copiedLink,
  canCopy,
  onCloudAnalyze,
  canCloud,
  analysisBusy,
  analysisOn,
  onCancelAnalysis,
  onHideAnalysis,
  onShareAnalysis,
  canShare,
  showBookHints,
  onToggleBookHints,
  showBookToggle,
  typeMoveOpen,
  onToggleTypeMove,
  showTypeMoveToggle,
}) {
  const { t } = useTranslation();

  return (
    <PlayingDisclosure
      title={t('playing.gameOptions', { defaultValue: 'Options' })}
      hint={t('playing.gameOptionsHint', { defaultValue: 'PGN, link, book hints…' })}
      open={open}
      onOpenChange={onOpenChange}
    >
      <div className="playing-options-list">
        <button
          type="button"
          className="playing-options-row"
          disabled={!canPgn}
          onClick={onDownloadPgn}
        >
          {t('playing.downloadPgn')}
        </button>
        <button
          type="button"
          className="playing-options-row"
          disabled={!canCopy}
          onClick={onCopyLink}
        >
          {copiedLink ? t('playing.copied') : t('playing.copyGameLink')}
        </button>
        {canCloud && (
          <button
            type="button"
            className="playing-options-row"
            disabled={analysisBusy || !canPgn}
            onClick={onCloudAnalyze}
          >
            {t('playing.analysisCloud')}
          </button>
        )}
        {analysisBusy && (
          <button type="button" className="playing-options-row" onClick={onCancelAnalysis}>
            {t('playing.cancelAnalysis')}
          </button>
        )}
        {analysisOn && !analysisBusy && (
          <button type="button" className="playing-options-row" onClick={onHideAnalysis}>
            {t('playing.hideAnalysis')}
          </button>
        )}
        {canShare && (
          <button type="button" className="playing-options-row" onClick={onShareAnalysis}>
            {t('playing.shareAnalysisBtn')}
          </button>
        )}
        {showBookToggle && (
          <button
            type="button"
            className={`playing-options-row${showBookHints ? ' playing-options-row--on' : ''}`}
            onClick={onToggleBookHints}
          >
            <span>{t('playing.showBookHints', { defaultValue: 'Opening book hints' })}</span>
            <span className="playing-options-row__state">
              {showBookHints ? t('common.on', { defaultValue: 'On' }) : t('common.off', { defaultValue: 'Off' })}
            </span>
          </button>
        )}
        {showTypeMoveToggle && (
          <button
            type="button"
            className={`playing-options-row${typeMoveOpen ? ' playing-options-row--on' : ''}`}
            onClick={onToggleTypeMove}
          >
            <span>{t('playing.typeMove')}</span>
            <span className="playing-options-row__state">
              {typeMoveOpen ? t('common.on', { defaultValue: 'On' }) : t('common.off', { defaultValue: 'Off' })}
            </span>
          </button>
        )}
      </div>
    </PlayingDisclosure>
  );
}

/** Keep rematch as primary buttons — they are requested outcomes after a game. */
export function PlayingRematchActions({
  finished,
  spectateOnly,
  humanGame,
  vsComputer,
  rematchBusy,
  rematchSent,
  rematchInboxError,
  incomingRematchChallenge,
  isParentRole,
  onRematchComputer,
  onRematchHuman,
  onAcceptRematch,
  onDeclineRematch,
  onRetryInbox,
  t,
}) {
  if (!finished || spectateOnly) return null;
  return (
    <>
      {vsComputer && (
        <Button variant="page-primary" onClick={onRematchComputer}>
          {t('playing.rematch')}
        </Button>
      )}
      {humanGame && rematchInboxError && !incomingRematchChallenge && (
        <p className="w-full text-xs text-amber-800">
          {rematchInboxError}
          {' '}
          <button type="button" className="font-semibold underline" onClick={onRetryInbox}>
            {t('playing.retry', { defaultValue: 'Retry' })}
          </button>
        </p>
      )}
      {humanGame && incomingRematchChallenge && (
        <>
          {!isParentRole && (
            <Button variant="page-primary" disabled={rematchBusy} onClick={onAcceptRematch}>
              {t('playing.acceptRematch', { defaultValue: 'Accept rematch' })}
            </Button>
          )}
          <Button variant="page-secondary" disabled={rematchBusy} onClick={onDeclineRematch}>
            {t('playing.decline')}
          </Button>
        </>
      )}
      {humanGame && !incomingRematchChallenge && !isParentRole && (
        <Button
          variant="page-primary"
          disabled={rematchBusy || rematchSent}
          onClick={onRematchHuman}
        >
          {rematchSent ? t('playing.rematchSent') : t('playing.rematch')}
        </Button>
      )}
    </>
  );
}
